import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import 'react-toastify/dist/ReactToastify.css';

import ProtectedRoute from './components/ProtectedRoute';
import { HAB } from './variables/habilitations';
import ReauthGate from './components/ReauthGate';
import GardeInactivite from './components/GardeInactivite';
import BandeauConnexionLente from './components/BandeauConnexionLente';
import AppelProvider from './components/appel/AppelProvider';

// Auth
import Login from './pages/auth/Login';
import Registration from './pages/auth/Registration';
import OTPPage from './pages/security/otp_page';
import MotDePasseOublie from './pages/auth/MotDePasseOublie';

// Core pages
import Dashboard from './pages/dashboard/Dashboard';
import TelechargerApplication from './pages/application-mobile/TelechargerApplication';
import ListSinistres from './pages/sinistres/ListSinistres';
import SinistresLies from './pages/sinistres/SinistresLies';
import FicheDeclaration from './pages/sinistres/FicheDeclaration';
import AttestationDeclaration from './pages/sinistres/AttestationDeclaration';
import AttestationNonGarantie from './pages/sinistres/AttestationNonGarantie';
import ConfirmationGarantie from './pages/sinistres/ConfirmationGarantie';
import DeclarationSinistre from './pages/sinistres/declaration/DeclarationSinistre';
import DeclarationsMobiles from './pages/sinistres/DeclarationsMobiles';
import AffectationsEnAttente from './pages/sinistres/AffectationsEnAttente';
import RattachementsAConfirmer from './pages/mobile/RattachementsAConfirmer';
import ComptesUsagers from './pages/mobile/ComptesUsagers';
import MesDiscussions from './pages/discussions/MesDiscussions';
import Conversations from './pages/conversations/Conversations';
import ListPvSinistres from './pages/pv-sinistres/ListPvSinistres';
import ListReclamations from './pages/reclamations/ListReclamations';
import DetailReclamation from './pages/reclamations/DetailReclamation';
import Delais from './pages/delais/Delais';
import Relances from './pages/relances/Relances';
import Performance from './pages/performance/Performance';
import Encaissements from './pages/encaissements/Encaissements';
import Attestations from './pages/attestations/Attestations';
import DetailCommandeAttestations from './pages/attestations/DetailCommande';
import CourrierLayout from './pages/courriers/CourrierLayout';
import Indemnisation from './pages/indemnisation/Indemnisation';
import Organismes from './pages/organismes/Organismes';
import Utilisateurs from './pages/utilisateurs/Utilisateurs';
import Pays from './pages/parametres/Pays';
import EcrituresComptables from './pages/comptabilite/EcrituresComptables';
import ComptesComptables from './pages/comptabilite/ComptesComptables';
import Balance from './pages/comptabilite/Balance';
import Fiscalite from './pages/comptabilite/Fiscalite';
import ParametrageEcritures from './pages/comptabilite/ParametrageEcritures';
import GedLayout from './pages/ged/GedLayout';
import Profil from './pages/account/Profil';
import NotFound from './pages/NotFound';
import RepriseSinistres from './pages/reprise/RepriseSinistres';
import RepriseEtatReclamations from './pages/reprise/RepriseEtatReclamations';
import EtatBapExercices from './pages/statistiques/EtatBapExercices';
import EtatSinistres from './pages/statistiques/EtatSinistres';
import EtatFinancier from './pages/statistiques/EtatFinancier';
import RepriseEncaissements from './pages/reprise/RepriseEncaissements';
import ParametragePieces from './pages/parametres/ParametragePieces';
import ReprisePaiements from './pages/reprise/ReprisePaiements';
import ParametresListe from './pages/parametres/ParametresListe';
import PublierApplication from './pages/parametres/PublierApplication';
import ParametresBanques from './pages/parametres/ParametresBanques';
import ParametresAttestations from './pages/parametres/ParametresAttestations';
import EntitesConstat from './pages/parametres/EntitesConstat';
import ParametresCima from './pages/parametres/ParametresCima';
import ParametreLayout from './pages/parametres/ParametreLayout';
import ConfigurationMessagerie from './pages/messagerie/ConfigurationMessagerie';
import Messagerie from './pages/messagerie/Messagerie';
import Profils from './pages/profils/Profils';
import ProfilCreate from './pages/profils/ProfilCreate';
import ProfilEdit from './pages/profils/ProfilEdit';
import Habilitations from './pages/habilitations/Habilitations';
import JournalActivite from './pages/journal-activite/JournalActivite';
import AppareilsAdmin from './pages/securite/AppareilsAdmin';
import ReportingMensuel from './pages/statistiques/ReportingMensuel';
import EtatCadence from './pages/statistiques/EtatCadences';
import EtatReclamations from './pages/statistiques/EtatReclamations';
import GraphiqueEncPai from './pages/statistiques/GraphiqueEncPai';
import ReclamationsTogo from './pages/statistiques/ReclamationsTogo';
import EtatLitiges from './pages/statistiques/EtatLitiges';
import Experts from './pages/experts/Experts';
import DetailsSinistre from './pages/sinistres/DetailsSinistre';
import OffreDetail from './pages/sinistres/OffreDetail';
import ReglementMultipleExperts from './pages/reglements-experts/ReglementMultipleExperts';
import LotReglementDetail from './pages/reglements-experts/LotReglementDetail';
import LotReglementsListe from './pages/reglements-experts/LotReglementsListe';
import ReconciliationImputations from './pages/admin/ReconciliationImputations';
import ListRencontres from './pages/bilaterales/ListRencontres';
import DetailsRencontre from './pages/bilaterales/DetailsRencontre';
import ListExpertises from './pages/expertises/ListExpertises';
import Paiements from './pages/paiements/Paiements';
import ListAttestationsDigitales from './pages/attestations-digitales/ListAttestationsDigitales';
import DetailAttestationDigitale from './pages/attestations-digitales/DetailAttestationDigitale';
import CommandesAttestationsDigitalesPage from './pages/attestations-digitales/CommandesAttestationsDigitalesPage';
import ClesApiPage from './pages/cles-api/ClesApiPage';
import AgentsVerbalisateursPage from './pages/agents-verbalisateurs/AgentsVerbalisateursPage';
import PortailVerification from './pages/verification/PortailVerification';
import BanniereEnvironnement from './components/BanniereEnvironnement';
import GardeMaintenance from './components/GardeMaintenance';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

/* `habilitation` doit etre transmise : sans elle, une route qui declare son droit
   le verrait avale ici en silence, et la garde ne s'appliquerait jamais. */
const Protected = ({ children, ...props }) => (
  <ProtectedRoute {...props}>{children}</ProtectedRoute>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Hors du Router, et volontairement : le bandeau ne dépend d'aucune route.
          Placé dans Layout, il aurait manqué à la connexion, à l'OTP et à
          l'activation, qui court-circuitent Layout - soit les écrans où savoir
          sur quel environnement on se trouve importe le plus. */}
      <BanniereEnvironnement />
      <GardeMaintenance />
      <ReauthGate />
      <GardeInactivite />
      <BrowserRouter>
        <AppelProvider>
        <Routes>
          {/* ── Public ── */}
          <Route path="/login" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route path="/otp-verify" element={<OTPPage />} />
          <Route path="/activation/:token" element={<Registration />} />
          <Route path="/verify/:token" element={<PortailVerification />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* ── Protected ── */}
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/application-mobile" element={<Protected><TelechargerApplication /></Protected>} />

          {/* Parametres */}
          <Route path="/parametres/pieces" element={<Protected><ParametragePieces /></Protected>} />
          <Route path="/parametres/application-mobile" element={<Protected><PublierApplication /></Protected>} />

          {/* Sinistres */}
          <Route path="/sinistres" element={<Protected><ListSinistres /></Protected>} />
          <Route path="/sinistres/nouveau" element={<Protected habilitation={HAB.SINISTRE_CREATE}><DeclarationSinistre /></Protected>} />
          {/* Avant /sinistres/:id par lisibilite : React Router classe de toute facon
              un segment litteral avant un segment dynamique, le chemin ne sera donc
              jamais pris pour un identifiant de dossier. Chemin impose par l'entree
              de menu (V51). */}
          <Route path="/sinistres/affectations-en-attente" element={<Protected><AffectationsEnAttente /></Protected>} />
          <Route path="/sinistres/:id/modifier" element={<Protected habilitation={HAB.SINISTRE_UPDATE}><DeclarationSinistre /></Protected>} />
          <Route path="/sinistres/:id" element={<Protected><DetailsSinistre /></Protected>} />
          <Route path="/sinistres/:id/fiche" element={<Protected><FicheDeclaration /></Protected>} />
          <Route path="/sinistres/:id/offre/:victimeId" element={<Protected><OffreDetail /></Protected>} />
          <Route path="/sinistres-lies" element={<Protected><SinistresLies /></Protected>} />
          <Route path="/sinistres/:id/attestation" element={<Protected><AttestationDeclaration /></Protected>} />
          <Route path="/sinistres/:id/non-garantie" element={<Protected><AttestationNonGarantie /></Protected>} />
          <Route path="/sinistres/:id/confirmation-garantie" element={<Protected><ConfirmationGarantie /></Protected>} />

          {/* Canal mobile - chemins imposés par le seed du menu (V202608050002) */}
          <Route path="/mobile/declarations" element={<Protected><DeclarationsMobiles /></Protected>} />
          {/* Ces deux entrees existaient au menu (seed V202608050002) SANS route :
              cliquer dessus menait a une page blanche. Le backend les servait
              pourtant depuis le debut. */}
          <Route path="/mobile/rattachements" element={<Protected><RattachementsAConfirmer /></Protected>} />
          <Route path="/mobile/usagers" element={<Protected><ComptesUsagers /></Protected>} />

          {/* Collaboration interne - chemin imposé par le seed du menu (V202608070001) */}
          <Route path="/discussions" element={<Protected><MesDiscussions /></Protected>} />
          <Route path="/conversations" element={<Protected><Conversations /></Protected>} />
          <Route path="/conversations/:id" element={<Protected><Conversations /></Protected>} />

          {/* PV */}
          <Route path="/pv-sinistres" element={<Protected><ListPvSinistres /></Protected>} />
          <Route path="/pv-sinistres/non-associes" element={<Protected><ListPvSinistres /></Protected>} />

          {/* Expertises */}
          <Route path="/expertises-medicales" element={<Protected><ListExpertises /></Protected>} />

          {/* Finance */}
          <Route path="/encaissements" element={<Protected><Encaissements /></Protected>} />
          <Route path="/paiements" element={<Protected><Paiements /></Protected>} />
          {/* Comptabilité — groupe de menu, un écran par entrée. L'ancienne URL
              unique renvoie vers les écritures (favoris, liens déjà partagés). */}
          <Route path="/comptabilite" element={<Navigate to="/comptabilite/ecritures" replace />} />
          <Route path="/comptabilite/comptes" element={<Protected><ComptesComptables /></Protected>} />
          <Route path="/comptabilite/ecritures" element={<Protected><EcrituresComptables /></Protected>} />
          <Route path="/comptabilite/balance" element={<Protected><Balance /></Protected>} />
          <Route path="/comptabilite/fiscalite" element={<Protected><Fiscalite /></Protected>} />
          <Route path="/comptabilite/parametrage" element={<Protected><ParametrageEcritures /></Protected>} />

          {/* Réclamations */}
          <Route path="/reclamations" element={<Protected><ListReclamations /></Protected>} />
          <Route path="/reclamations/:id" element={<Protected><DetailReclamation /></Protected>} />

          {/* Indemnisation */}
          <Route path="/indemnisation" element={<Protected><Indemnisation /></Protected>} />

          {/* Attestations physiques */}
          <Route path="/attestations" element={<Protected><Attestations /></Protected>} />
          <Route path="/attestations/commandes/:commandeId" element={<Protected><DetailCommandeAttestations /></Protected>} />

          {/* Digitalisation - Attestations digitales */}
          <Route path="/attestations-digitales" element={<Protected><ListAttestationsDigitales /></Protected>} />
          <Route path="/attestations-digitales/:trackingId" element={<Protected><DetailAttestationDigitale /></Protected>} />
          <Route path="/attestations-digitales/commandes" element={<Protected><CommandesAttestationsDigitalesPage /></Protected>} />
          <Route path="/cles-api" element={<Protected><ClesApiPage /></Protected>} />
          <Route path="/agents-verbalisateurs" element={<Protected><AgentsVerbalisateursPage /></Protected>} />

          {/* Opérations */}
          <Route path="/bilaterales" element={<Protected><ListRencontres /></Protected>} />
          <Route path="/bilaterales/:id" element={<Protected><DetailsRencontre /></Protected>} />
          {/* Les trois chemins rendent la meme page-hub ; l'onglet se deduit de l'URL. */}
          <Route path="/courriers" element={<Protected><CourrierLayout /></Protected>} />
          <Route path="/courriers/bordereaux" element={<Protected><CourrierLayout /></Protected>} />
          <Route path="/courriers/registre" element={<Protected><CourrierLayout /></Protected>} />
          {/* Les deux chemins rendent la meme page-hub ; l'onglet se deduit de l'URL. */}
          <Route path="/ged" element={<Protected><GedLayout /></Protected>} />
          <Route path="/ged/dossiers" element={<Protected><GedLayout /></Protected>} />

          {/* Délais */}
          <Route path="/delais" element={<Protected><Delais /></Protected>} />
          <Route path="/relances" element={<Protected><Relances /></Protected>} />
          <Route path="/performance" element={<Protected><Performance /></Protected>} />

          {/* Admin */}
          <Route path="/admin/reconciliation-imputations/:sinistreId" element={<Protected><ReconciliationImputations /></Protected>} />
          <Route path="/organismes" element={<Protected><Organismes /></Protected>} />
          <Route path="/utilisateurs" element={<Protected><Utilisateurs /></Protected>} />
          <Route path="/parametres/pays" element={<Protected><Pays /></Protected>} />
          <Route path="/parametres/banques" element={<Protected><ParametresBanques /></Protected>} />
          <Route path="/parametres/attestations" element={<Protected><ParametresAttestations /></Protected>} />
          <Route path="/parametres/listes" element={<Protected><ParametresListe /></Protected>} />
          <Route path="/parametres/entites-constat" element={<Protected><EntitesConstat /></Protected>} />
          <Route path="/parametres/baremes" element={<Protected><ParametresCima /></Protected>} />
          <Route path="/parametres" element={<Protected><ParametreLayout /></Protected>} />

          {/* Profil */}
          <Route path="/mon-profil" element={<Protected><Profil /></Protected>} />
          <Route path="/mon-profil/securite" element={<Protected><Profil /></Protected>} />

          {/* Reprise de donnees */}
          <Route path="/reprise/sinistres" element={<Protected><RepriseSinistres /></Protected>} />
          <Route path="/reprise/encaissements" element={<Protected><RepriseEncaissements /></Protected>} />
          <Route path="/reprise/paiements" element={<Protected><ReprisePaiements /></Protected>} />
          <Route path="/reprise/etat-reclamations" element={<Protected><RepriseEtatReclamations /></Protected>} />

          {/* Statistiques */}
          <Route path="/stats/sinistres" element={<Protected><EtatSinistres /></Protected>} />
          <Route path="/stats/financier" element={<Protected><EtatFinancier /></Protected>} />
          <Route path="/stats/reporting" element={<Protected><ReportingMensuel /></Protected>} />
          <Route path="/stats/cadence" element={<Protected><EtatCadence /></Protected>} />
          <Route path="/stats/reclamations" element={<Protected><EtatReclamations /></Protected>} />
          <Route path="/stats/bap-exercices" element={<Protected><EtatBapExercices /></Protected>} />
          <Route path="/stats/graphique-enc-pai" element={<Protected><GraphiqueEncPai /></Protected>} />
          <Route path="/stats/reclamations-togo" element={<Protected><ReclamationsTogo /></Protected>} />
          <Route path="/stats/litiges" element={<Protected><EtatLitiges /></Protected>} />

          {/* Paramétrages */}
          <Route path="/parametres/listes" element={<Protected><ParametresListe /></Protected>} />
          {/* MEME CAS QUE << /reglements-multiple-experts >> : aucune entree de menu ne
              couvre ce chemin - l'ecran est atteint par un bouton - et la garde le
              refusait donc a tout le monde. EXPERT_READ est detenu par les neuf
              profils : la declaration ouvre l'ecran sans exclure personne. */}
          <Route path="/experts" element={<Protected habilitation={HAB.EXPERT_READ}><Experts /></Protected>} />

          {/* Règlements aux experts */}
          {/* HABILITATION DECLAREE, parce qu'aucune entree de menu ne couvre ce chemin.
              La garde autorise une route par PREFIXE d'une entree de menu ; cet ecran
              n'est atteint que par un bouton, et aucun chemin du menu ne le prefixe -
              « /lot-reglements » ne couvre pas « /reglements-multiple-experts ». Il
              etait donc refuse a TOUT LE MONDE, en 403.
              LOT_REGLEMENT_CREATE et non _READ : cet ecran existe pour creer un lot,
              et c'est ce droit qu'exige le POST. Le laisser entrer avec la seule
              lecture l'aurait fait remplir le formulaire pour se voir refuser a
              l'enregistrement. */}
          <Route path="/reglements-multiple-experts" element={<Protected habilitation={HAB.LOT_REGLEMENT_CREATE}><ReglementMultipleExperts /></Protected>} />
          <Route path="/lot-reglements" element={<Protected><LotReglementsListe /></Protected>} />
          <Route path="/lot-reglements/:lotTrackingId" element={<Protected><LotReglementDetail /></Protected>} />

          {/* Messagerie mail */}
          <Route path="/messagerie/config" element={<Protected><ConfigurationMessagerie /></Protected>} />
          <Route path="/messagerie" element={<Protected><Messagerie /></Protected>} />

          {/* Profils et Habilitations sont deux referentiels distincts - un role
              qu'on attribue, un droit unitaire qu'on coche - et ne passent plus par
              un ecran a deux volets. Chaque ecran s'enveloppe deja dans <Layout> :
              il suffisait de cesser de les embarquer. */}
          <Route path="/profils" element={<Protected><Profils /></Protected>} />
          <Route path="/profils/new" element={<Protected><ProfilCreate /></Protected>} />
          <Route path="/profils/:id/edit" element={<Protected><ProfilEdit /></Protected>} />

          <Route path="/habilitations" element={<Protected><Habilitations /></Protected>} />
          <Route path="/journal-activite" element={<Protected><JournalActivite /></Protected>} />
          <Route path="/securite/appareils" element={<Protected><AppareilsAdmin /></Protected>} />
          {/* Anciennes URL conservees : un favori ou un lien colle dans un mail ne
              doit pas tomber en 404 parce qu'on a range le menu autrement. */}
          <Route path="/profils-habilitations" element={<Navigate to="/profils" replace />} />
          <Route path="/profils/habilitations" element={<Navigate to="/habilitations" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </AppelProvider>
      </BrowserRouter>

      <BandeauConnexionLente />

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{ borderRadius: 10, fontSize: 14 }}
      />
    </QueryClientProvider>
  );
}